import { useState, useEffect, useRef } from 'react';
import foxScenariusz from '@/assets/fox-scenariusz.webp';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/hooks/useAuth";
import { useLibraryContent } from "@/hooks/useLibraryContent";
import { useGeneratedContent } from "@/hooks/useGeneratedContent";
import { useTrashContent } from "@/hooks/useTrashContent";
import { useTrashManagement } from "@/hooks/useTrashManagement";
// DashboardSidebar and DashboardHeader are now in DashboardLayout
import { ContentCard } from "@/components/ContentCard";
import { LibrarySkeleton } from "@/components/skeletons/LibrarySkeleton";
import { FavoriteCompactCard } from "@/components/FavoriteCompactCard";
import { ContentDetailModal } from "@/components/ContentDetailModal";
import { AllIdeasSlider } from "@/components/AllIdeasSlider";
import { CorkBoardView } from "@/components/CorkBoardView";
import { CorkModernView } from "@/components/CorkModernView";
import TopIdeasBox from "@/components/TopIdeasBox";
import FavoriteIdeasBox from "@/components/FavoriteIdeasBox";
import { ContentIdea } from "@/types/dashboard";
import { useLanguage } from "@/contexts/LanguageContext";
import { TabButton } from "@/components/ui/tab-button";
import { 
  Sparkles, 
  Filter,
  Star,
  TrendingUp,
  Heart,
  RefreshCw,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  List,
  Info
} from "lucide-react";

const Library = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, language } = useLanguage();
  
  // Read tab from URL, default to 'all'
  const tabFromUrl = (searchParams.get('tab') || 'all') as 'all' | 'best' | 'used' | 'generated' | 'trash';
  const [activeTab, setActiveTab] = useState<'all' | 'best' | 'used' | 'generated' | 'trash'>(tabFromUrl);
  const [localFilters, setLocalFilters] = useState({
    sourceAccount: 'all',
    viralScore: 'all',
    dateSort: 'newest',
    sortBy: 'date',
    timeRange: 'all'
  });
  const [selectedIdea, setSelectedIdea] = useState<ContentIdea | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [viewMode, setViewMode] = useState<'cork_classic' | 'cork_modern' | 'list'>(() => {
    const viewFromUrl = searchParams.get('view');
    if (viewFromUrl === 'cork_classic' || viewFromUrl === 'cork_modern' || viewFromUrl === 'list') return viewFromUrl;
    // Migrate old 'cork' value to 'cork_classic'
    if (viewFromUrl === 'cork') return 'cork_classic';
    const saved = localStorage.getItem('biblioteka_view_mode');
    if (saved === 'cork_classic' || saved === 'cork_modern' || saved === 'list') return saved;
    // Migrate old saved value
    if (saved === 'cork') return 'cork_classic';
    return 'cork_modern';
  });
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Sync activeTab and viewMode with URL changes
  useEffect(() => {
    const tabFromUrl = (searchParams.get('tab') || 'all') as 'all' | 'best' | 'used' | 'generated' | 'trash';
    const viewFromUrl = searchParams.get('view');
    setActiveTab(tabFromUrl);
    if (viewFromUrl === 'cork_classic' || viewFromUrl === 'cork_modern' || viewFromUrl === 'list') {
      setViewMode(viewFromUrl);
    } else if (viewFromUrl === 'cork') {
      setViewMode('cork_classic');
    }
  }, [searchParams]);

  const {
    isLoading,
    contentIdeas,
    error,
    lastRefresh,
    copyToClipboard,
    refresh
  } = useLibraryContent(user?.id || null);

  const {
    isLoading: isLoadingGenerated,
    generatedIdeas,
    error: errorGenerated,
    refresh: refreshGenerated
  } = useGeneratedContent(user?.id || null);

  const {
    isLoading: isLoadingTrash,
    trashIdeas,
    error: errorTrash,
    refresh: refreshTrash
  } = useTrashContent(user?.id || null);

  const { moveToTrash, restoreFromTrash, isLoading: isTrashOperationLoading } = useTrashManagement();

  // Filter ideas based on active tab
  const tabFilteredIdeas = activeTab === 'generated'
    ? generatedIdeas
    : activeTab === 'used' 
    ? contentIdeas.filter(idea => idea.is_used)
    : activeTab === 'best'
    ? contentIdeas.filter(idea => !idea.is_used)
    : activeTab === 'trash'
    ? trashIdeas
    : contentIdeas;

  // Get recent favorites (last 3) from all content (not filtered by tab)
  const recentFavorites = contentIdeas
    .filter(idea => idea.is_favorited)
    .sort((a, b) => new Date(b.favorited_at || 0).getTime() - new Date(a.favorited_at || 0).getTime())
    .slice(0, 3);

  // Get top 3 ideas by viral score from tab-filtered content
  const topIdeas = tabFilteredIdeas
    .sort((a, b) => (b.viral_score || 0) - (a.viral_score || 0))
    .slice(0, 3);

  // Get IDs to exclude from slider (top 3 and favorites)
  const excludedIds = new Set([
    ...recentFavorites.map(idea => idea.id),
    ...topIdeas.map(idea => idea.id)
  ]);

  // Filter ideas for the slider (exclude top 3 and favorites, use tab-filtered)
  const sliderIdeas = tabFilteredIdeas.filter(idea => !excludedIds.has(idea.id));

  // Apply local filters to tab-filtered content
  const filteredContent = tabFilteredIdeas.filter(idea => {
    // Source account filter
    if (localFilters.sourceAccount !== 'all') {
      const sourceMatch = idea.source_competitor?.username === localFilters.sourceAccount;
      if (!sourceMatch) return false;
    }

    // Viral score filter
    if (localFilters.viralScore !== 'all') {
      const score = idea.viral_score || 0;
      switch (localFilters.viralScore) {
        case 'high':
          if (score < 70) return false;
          break;
        case 'medium':
          if (score < 40 || score >= 70) return false;
          break;
        case 'low':
          if (score >= 40) return false;
          break;
      }
    }

    // Time range filter
    if (localFilters.timeRange !== 'all') {
      const ideaDate = new Date(idea.created_at);
      const now = new Date();
      const daysAgo = (now.getTime() - ideaDate.getTime()) / (1000 * 60 * 60 * 24);
      
      switch (localFilters.timeRange) {
        case 'week':
          if (daysAgo > 7) return false;
          break;
        case '2weeks':
          if (daysAgo > 14) return false;
          break;
        case 'month':
          if (daysAgo > 30) return false;
          break;
        case '3months':
          if (daysAgo > 90) return false;
          break;
      }
    }

    return true;
  }).sort((a, b) => {
    // Sorting
    if (localFilters.sortBy === 'viral') {
      // Sort by viral score (highest first)
      const scoreA = a.viral_score || 0;
      const scoreB = b.viral_score || 0;
      return scoreB - scoreA;
    } else {
      // Sort by date
      if (localFilters.dateSort === 'newest') {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      } else {
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      }
    }
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredContent.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedContent = filteredContent.slice(startIndex, endIndex);

  // Reset to page 1 when filters or tab change
  useEffect(() => {
    setCurrentPage(1);
  }, [localFilters, itemsPerPage, activeTab]);

  // Scroll to top when page changes
  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  // Update selectedIdea when underlying data changes (after refresh)
  useEffect(() => {
    if (selectedIdea && isModalOpen) {
      // Find the updated idea from the refreshed data
      const allIdeas = [...contentIdeas, ...generatedIdeas];
      const updatedIdea = allIdeas.find(idea => idea.id === selectedIdea.id);
      if (updatedIdea) {
        setSelectedIdea(updatedIdea);
      }
    }
  }, [contentIdeas, generatedIdeas]);


  // Get unique source accounts for filter dropdown from tab-filtered ideas
  const sourceAccounts = Array.from(
    new Set(
      tabFilteredIdeas
        .map(idea => idea.source_competitor?.username)
        .filter(Boolean)
    )
  );

  const handleIdeaClick = (idea: ContentIdea) => {
    setSelectedIdea(idea);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedIdea(null);
  };

  const handleTrashAction = async (ideaId: string, source: 'scraped' | 'user_generated') => {
    if (activeTab === 'trash') {
      const success = await restoreFromTrash(ideaId, source);
      if (success) {
        // Refresh data in background without blocking UI
        // Using setTimeout to prevent blocking the main thread
        setTimeout(() => {
          refreshTrash();
          refresh();
          refreshGenerated();
        }, 0);
      }
    } else {
      const success = await moveToTrash(ideaId, source);
      if (success) {
        // Refresh data in background without blocking UI
        setTimeout(() => {
          refresh();
          refreshGenerated();
          refreshTrash();
        }, 0);
      }
    }
  };

  // Check if we have data already (only show loading on initial load, not on tab changes)
  const hasData = contentIdeas.length > 0 || generatedIdeas.length > 0 || trashIdeas.length > 0;
  const isInitialLoading = (isLoading || isLoadingGenerated || isLoadingTrash) && !hasData;

  // Loading State - only show on initial load when we have no data yet
  if (isInitialLoading) {
    return <LibrarySkeleton />;
  }

  // Error State
  if (error || errorGenerated || errorTrash) {
    return (
      <main className="flex-1 p-3 md:p-6 overflow-y-auto">
        <div className="text-center space-y-6">
          <div className="w-16 h-16 mx-auto bg-destructive rounded-full flex items-center justify-center">
            <RefreshCw className="h-8 w-8 text-destructive-foreground" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              {t('library.failedToLoad')}
            </h1>
            <p className="text-xl text-muted-foreground mb-6">
              {error || errorGenerated || errorTrash}
            </p>
            <Button onClick={() => { refresh(); refreshGenerated(); refreshTrash(); }} size="lg">
              <RefreshCw className="mr-2 h-5 w-5" />
              {t('library.tryAgain')}
            </Button>
          </div>
        </div>
      </main>
    );
  }

  // Empty State
  if (contentIdeas.length === 0 && generatedIdeas.length === 0) {
    return (
      <main className="flex-1 p-3 md:p-6 overflow-y-auto">
        <div className="text-center space-y-6 max-w-lg mx-auto pt-8">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">
            Uzupełnij źródła inspiracji
          </h1>
          <p className="text-muted-foreground">
            Dodaj profile Instagram, z których chcesz czerpać inspiracje - wygenerujemy dla Ciebie pomysły na treści
          </p>
          <img 
            src={foxScenariusz} 
            alt="Fox scenariusz" 
            className="w-48 h-48 mx-auto object-contain"
          />
          <Button onClick={() => navigate('/inspiracje')} size="lg">
            Uzupełnij
          </Button>
        </div>
      </main>
    );
  }

  return (
    <>
      {/* Content Area */}
      <main ref={scrollContainerRef} className="flex-1 p-3 md:p-6 pb-16 flex justify-center overflow-y-auto">
          <div className="w-full max-w-[1200px] space-y-3 pb-16">

            {/* Tabs Navigation */}
            <div className="glass-card rounded-xl p-1 border border-border/50 inline-flex flex-wrap gap-1">
              <TabButton
                isActive={activeTab === 'all'}
                onClick={() => {
                  setActiveTab('all');
                  setSearchParams({ tab: 'all' });
                }}
                variant="gradient"
              >
                {t('library.tabs.all')}
              </TabButton>
              <TabButton
                isActive={activeTab === 'generated'}
                onClick={() => {
                  setActiveTab('generated');
                  setSearchParams({ tab: 'generated' });
                }}
                variant="gradient"
              >
                {t('library.tabs.generated')}
              </TabButton>
              <TabButton
                isActive={activeTab === 'best'}
                onClick={() => {
                  setActiveTab('best');
                  setSearchParams({ tab: 'best' });
                }}
                variant="gradient"
              >
                {t('library.tabs.best')}
              </TabButton>
              <TabButton
                isActive={activeTab === 'used'}
                onClick={() => {
                  setActiveTab('used');
                  setSearchParams({ tab: 'used' });
                }}
                variant="gradient"
              >
                {t('library.tabs.used')}
              </TabButton>
              <TabButton
                isActive={activeTab === 'trash'}
                onClick={() => {
                  setActiveTab('trash');
                  setSearchParams({ tab: 'trash' });
                }}
                variant="gradient"
                className="flex items-center gap-1.5"
              >
                <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                {t('library.tabs.trash')}
              </TabButton>
            </div>

            {/* Trash Info Box - Only show on "trash" tab */}
            {activeTab === 'trash' && (
              <Alert className="bg-muted/50 border-border/50">
                <Info className="h-4 w-4 text-primary" />
                <AlertDescription className="text-sm text-muted-foreground ml-2">
                  {language === 'pl' 
                    ? 'Pomysły odrzucone i przeniesione do kosza zostaną automatycznie usunięte w ciągu 30 dni.'
                    : 'Rejected ideas moved to trash will be automatically deleted within 30 days.'}
                </AlertDescription>
              </Alert>
            )}

            {/* Top Ideas Box - Only show on "best" tab */}
            {activeTab === 'best' && (
              <TopIdeasBox 
                ideas={tabFilteredIdeas}
                onIdeaClick={handleIdeaClick}
                limit={10}
                onSeeMore={() => {
                  setLocalFilters({
                    sourceAccount: 'all',
                    viralScore: 'high',
                    dateSort: 'newest',
                    sortBy: 'viral',
                    timeRange: 'all'
                  });
                  setTimeout(() => {
                    document.getElementById('content-grid')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
              />
            )}

            {/* View Toggle - Show on all tabs except best and trash */}
            {activeTab !== 'best' && activeTab !== 'trash' && (
              <div className="glass-card p-3 rounded-xl border border-border/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <LayoutGrid className="h-4 w-4 text-primary" />
                    <span className="font-semibold text-foreground text-sm">{t('library.view')}</span>
                  </div>
                  
                  <div className="flex items-center">
                    {/* Cork Classic Button */}
                    <button
                      onClick={() => {
                        setViewMode('cork_classic');
                        localStorage.setItem('biblioteka_view_mode', 'cork_classic');
                        const newParams = new URLSearchParams(searchParams);
                        newParams.set('view', 'cork_classic');
                        setSearchParams(newParams);
                      }}
                      className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-all rounded-l-lg border ${
                        viewMode === 'cork_classic' 
                          ? 'bg-blue-600 text-white border-blue-600' 
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <span>📌</span>
                      <span className="hidden sm:inline">Cork Classic</span>
                    </button>
                    {/* Cork Modern Button */}
                    <button
                      onClick={() => {
                        setViewMode('cork_modern');
                        localStorage.setItem('biblioteka_view_mode', 'cork_modern');
                        const newParams = new URLSearchParams(searchParams);
                        newParams.set('view', 'cork_modern');
                        setSearchParams(newParams);
                      }}
                      className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-all border-t border-b ${
                        viewMode === 'cork_modern' 
                          ? 'bg-blue-600 text-white border-blue-600' 
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <span>✨</span>
                      <span className="hidden sm:inline">Cork Modern</span>
                    </button>
                    {/* List Button */}
                    <button
                      onClick={() => {
                        setViewMode('list');
                        localStorage.setItem('biblioteka_view_mode', 'list');
                        const newParams = new URLSearchParams(searchParams);
                        newParams.set('view', 'list');
                        setSearchParams(newParams);
                      }}
                      className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-all rounded-r-lg border ${
                        viewMode === 'list' 
                          ? 'bg-blue-600 text-white border-blue-600' 
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <List className="h-4 w-4" />
                      <span className="hidden sm:inline">Lista</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Filters - Hide on "best" tab */}
            {activeTab !== 'best' && (
            <div className="glass-card p-3 rounded-xl border border-border/50">
                <div className="flex flex-col gap-3">
                  {/* Top Row: Header and Actions */}
                  <div className="flex items-center justify-between">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowFilters(!showFilters)}
                      className="flex items-center gap-2 h-8 text-sm"
                    >
                      <Filter className="h-4 w-4 text-primary" />
                      <span className="font-semibold text-foreground">{t('library.filters')}</span>
                      <ChevronRight className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-90' : ''}`} />
                    </Button>
                    
                    <div className="flex items-center gap-2">
                      {showFilters && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setLocalFilters({
                            sourceAccount: 'all',
                            viralScore: 'all',
                            dateSort: 'newest',
                            sortBy: 'date',
                            timeRange: 'all'
                          })}
                          className="h-8 text-xs"
                        >
                          {t('common.clear')}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Filters Row - Wrapping grid */}
                  {showFilters && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {/* Viral Score Filter */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs text-muted-foreground">{t('library.score')}</label>
                      <Select
                        value={localFilters.viralScore}
                        onValueChange={(value) => setLocalFilters(prev => ({ ...prev, viralScore: value }))}
                      >
                        <SelectTrigger className="h-9 w-full text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('library.allScores')}</SelectItem>
                          <SelectItem value="high">Wysoki (70+)</SelectItem>
                          <SelectItem value="medium">Średni (40-69)</SelectItem>
                          <SelectItem value="low">Niski (0-39)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Time Range Filter */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs text-muted-foreground">Okres</label>
                      <Select
                        value={localFilters.timeRange}
                        onValueChange={(value) => setLocalFilters(prev => ({ ...prev, timeRange: value }))}
                      >
                        <SelectTrigger className="h-9 w-full text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Wszystkie</SelectItem>
                          <SelectItem value="week">Ostatni tydzień</SelectItem>
                          <SelectItem value="2weeks">2 tygodnie</SelectItem>
                          <SelectItem value="month">Miesiąc</SelectItem>
                          <SelectItem value="3months">3 miesiące</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Source Account Filter */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs text-muted-foreground">{t('library.source')}</label>
                      <Select
                        value={localFilters.sourceAccount}
                        onValueChange={(value) => setLocalFilters(prev => ({ ...prev, sourceAccount: value }))}
                      >
                        <SelectTrigger className="h-9 w-full text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('library.allSources')}</SelectItem>
                          {sourceAccounts.map(account => (
                            <SelectItem key={account} value={account || ''}>
                              @{account}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Sort By */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs text-muted-foreground">Sortuj</label>
                      <Select
                        value={localFilters.sortBy}
                        onValueChange={(value) => setLocalFilters(prev => ({ ...prev, sortBy: value }))}
                      >
                        <SelectTrigger className="h-9 w-full text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="date">Po dacie</SelectItem>
                          <SelectItem value="viral">Najlepsze</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Date Sort - Only show when sorting by date */}
                    {localFilters.sortBy === 'date' && (
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs text-muted-foreground">{t('library.date')}</label>
                        <Select
                          value={localFilters.dateSort}
                          onValueChange={(value) => setLocalFilters(prev => ({ ...prev, dateSort: value }))}
                        >
                          <SelectTrigger className="h-9 w-full text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="newest">{t('library.newestFirst')}</SelectItem>
                            <SelectItem value="oldest">{t('library.oldestFirst')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                  )}
                </div>
            </div>
            )}

            {/* Empty state for Inspiracje tab when no inspiration content but has generated content */}
            {activeTab === 'all' && contentIdeas.length === 0 && generatedIdeas.length > 0 ? (
              <div className="glass-card rounded-xl p-6 border border-border/50">
                <div className="text-center space-y-4 py-6">
                  <div className="w-12 h-12 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
                    <Sparkles className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      Dodaj źródła inspiracji
                    </h3>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto">
                      Dodaj profile Instagram, z których chcesz czerpać inspiracje - wygenerujemy dla Ciebie pomysły na treści oparte na sprawdzonych formatach
                    </p>
                  </div>
                  <Button 
                    onClick={() => navigate('/inspiracje')} 
                    className="mt-2"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Dodaj inspiracje
                  </Button>
                </div>
              </div>
            ) : (activeTab !== 'best' && activeTab !== 'trash') && viewMode === 'cork_classic' ? (
              <CorkBoardView 
                ideas={filteredContent}
                onIdeaClick={handleIdeaClick}
              />
            ) : (activeTab !== 'best' && activeTab !== 'trash') && viewMode === 'cork_modern' ? (
              <CorkModernView
                ideas={filteredContent}
                onIdeaClick={handleIdeaClick}
                onFavoriteToggle={() => { refresh(); refreshGenerated(); }}
                onUsedToggle={() => { refresh(); refreshGenerated(); }}
              />
            ) : (activeTab !== 'best' && activeTab !== 'trash') && viewMode === 'list' ? (
              <>
                {/* Content Count and Items Per Page */}
                <div className="flex items-center justify-between px-1">
              <p className="text-sm text-muted-foreground">
                {language === 'pl' 
                  ? `${startIndex + 1}-${Math.min(endIndex, filteredContent.length)} z ${filteredContent.length}`
                  : `${startIndex + 1}-${Math.min(endIndex, filteredContent.length)} of ${filteredContent.length}`
                }
              </p>
              
              <Select
                value={itemsPerPage.toString()}
                onValueChange={(value) => setItemsPerPage(Number(value))}
              >
                <SelectTrigger className="h-8 w-20 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="30">30</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Content Grid - SECOND */}
            <div id="content-grid"></div>
            <div className="bg-card rounded-2xl p-4 md:p-6 shadow-sm border border-border/30">
              <div className="flex flex-col gap-4">
              {paginatedContent.map((idea, index) => (
                <div key={idea.id} data-tour={index === 0 ? "content-card" : undefined}>
                  <ContentCard
                    idea={idea}
                    onCopyScript={(content) => copyToClipboard(content, 'Script')}
                    onFavoriteToggle={() => {
                      if (activeTab === 'generated') {
                        refreshGenerated();
                      } else {
                        refresh();
                      }
                    }}
                    isRowLayout={true}
                    isExpanded={expandedCardId === idea.id}
                    onToggleExpand={() => setExpandedCardId(expandedCardId === idea.id ? null : idea.id)}
                    isTrashView={false}
                    onTrashAction={handleTrashAction}
                  />
                </div>
              ))}
              </div>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                    // Show first page, last page, current page, and pages around current
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <Button
                          key={page}
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(page)}
                          className="w-10"
                        >
                          {page}
                        </Button>
                      );
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return <span key={page} className="px-2">...</span>;
                    }
                    return null;
                  })}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Empty Filtered Results */}
            {filteredContent.length === 0 && contentIdeas.length > 0 && (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto bg-muted rounded-full flex items-center justify-center mb-4">
                  <Filter className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {t('library.noMatches')}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {t('library.noMatchesDesc')}
                </p>
                <Button
                  variant="outline"
                  onClick={() => setLocalFilters({
                    sourceAccount: 'all',
                    viralScore: 'all',
                    dateSort: 'newest',
                    sortBy: 'date',
                    timeRange: 'all'
                  })}
                >
                  {t('library.clearAllFilters')}
                </Button>
              </div>
            )}

            {/* Stats Footer */}
            <div className="glass-card p-8 rounded-2xl border border-white/10 shadow-elevated">
              <div className="flex items-center justify-center gap-12 text-sm flex-wrap">
                <div className="flex items-center gap-3 group hover:scale-110 transition-transform duration-300">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10">
                    <TrendingUp className="h-5 w-5 text-primary" />
                  </div>
                  <div className="text-left">
                    <div className="text-2xl font-bold text-foreground">{contentIdeas.length}</div>
                    <div className="text-xs text-muted-foreground">{t('library.totalIdeas')}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 group hover:scale-110 transition-transform duration-300">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-red-500/20 to-pink-500/20">
                    <Heart className="h-5 w-5 text-red-500" />
                  </div>
                  <div className="text-left">
                    <div className="text-2xl font-bold text-foreground">{recentFavorites.length}</div>
                    <div className="text-xs text-muted-foreground">{t('library.favorites')}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 group hover:scale-110 transition-transform duration-300">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-yellow-500/20 to-orange-500/20">
                    <Star className="h-5 w-5 text-yellow-500" />
                  </div>
                  <div className="text-left">
                    <div className="text-2xl font-bold text-foreground">
                      {contentIdeas.length > 0 
                        ? Math.round(
                            contentIdeas.reduce((sum, idea) => sum + (idea.viral_score || 0), 0) / contentIdeas.length
                          ) 
                        : 0}
                    </div>
                    <div className="text-xs text-muted-foreground">{t('library.avgScore')}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* All Ideas Slider - LAST */}
            <AllIdeasSlider 
              ideas={sliderIdeas}
              onIdeaClick={handleIdeaClick}
            />
              </>
            ) : activeTab === 'trash' ? (
              <>
                {/* Trash Tab Content */}
                <div className="bg-card rounded-2xl p-4 md:p-6 shadow-sm border border-border/30">
                  <div className="flex flex-col gap-4">
                    {filteredContent.length === 0 ? (
                      <div className="text-center py-20">
                        <div className="w-16 h-16 mx-auto bg-muted rounded-full flex items-center justify-center mb-4">
                          <Trash2 className="h-8 w-8 text-muted-foreground" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground mb-2">
                          {language === 'pl' ? 'Kosz jest pusty' : 'Trash is empty'}
                        </h3>
                        <p className="text-muted-foreground">
                          {language === 'pl' 
                            ? 'Przeniesione pomysły pojawią się tutaj' 
                            : 'Trashed ideas will appear here'}
                        </p>
                      </div>
                    ) : (
                      filteredContent.map((idea) => (
                        <ContentCard
                          key={idea.id}
                          idea={idea}
                          onCopyScript={(content) => copyToClipboard(content, 'Script')}
                          onFavoriteToggle={() => {
                            refreshTrash();
                            refresh();
                            refreshGenerated();
                          }}
                          isRowLayout={true}
                          isExpanded={expandedCardId === idea.id}
                          onToggleExpand={() => setExpandedCardId(expandedCardId === idea.id ? null : idea.id)}
                          isTrashView={true}
                          onTrashAction={handleTrashAction}
                        />
                      ))
                    )}
                  </div>
                </div>
              </>
            ) : null}
            </div>
          </main>

        {/* Content Detail Modal */}
        <ContentDetailModal
          idea={selectedIdea}
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onCopyScript={(content) => copyToClipboard(content, 'Script')}
          onFavoriteToggle={() => {
            refresh();
            refreshGenerated();
          }}
          onUsedToggle={() => {
            refresh();
          }}
          onTrash={handleTrashAction}
        />
    </>
    );
};

export default Library;